import React from "react";
import {
  AveragePrice,
  ToggleButton,
  Circle,
  Content,
  Header,
  LivingCostColumn,
  LivingCosts,
  Number,
  PercentageChangeWrapper,
  Prices,
  PricesHeading,
  StyledLocationDetail,
  Title,
  TitleWrapper,
  LivingCostHeading,
  LivingCostValue,
  LivingCostsHeadlines,
  ButtonWrapper,
  HouseTypePricesWrapper,
  ColumnHeader,
  Image,
  SocialIcons,
  SocialButton,
  SocialImage,
  ShareText,
  ActionsWrapper,
} from "./LocationDetail.styles";

import FacebookIcon from "./img/facebook-icon.svg";
import TwitterIcon from "./img/twitter-icon.svg";

import PropTypes from "prop-types";
import PercentageChange from "../PercentageChange";
import Button from "../Button";

import detachedImage from "./img/detached.svg";
import semiDetachedImage from "./img/semi-detached.svg";
import terracedImage from "./img/terraced.svg";
import flatsImage from "./img/flats.svg";
import { useMedia } from "react-use";
import { device } from "../../device";
import HouseTypePrice from "../HouseTypePrice";
import { useTranslation } from "react-i18next";
import tickImage from "./img/tick.svg";
import { lifeSatisifactionFriendly } from "../../utils";
import Carousel from "../Carousel";

const houseTypeImages = [
  detachedImage,
  semiDetachedImage,
  terracedImage,
  flatsImage,
];

const HouseTypeList = ({ types }) => {
  const { t } = useTranslation();

  return (
    <HouseTypePricesWrapper>
      {types.map((item, key) => (
        <HouseTypePrice
          key={item.name}
          image={item.image}
          title={t(`location.houseTypes.${item.name}`)}
          value={parseInt(item.average)}
        />
      ))}
    </HouseTypePricesWrapper>
  );
};

const LocationDetail = ({
  id,
  location,
  detail,
  active,
  onToggle,
  ...rest
}) => {
  const { t } = useTranslation();

  const isLargerDevice = useMedia(device.mobileL);

  const handleToggle = () => {
    onToggle(id, !active);
  };

  /**
   * Bit of a hack.
   */
  const handleBackToTop = (e) => {
    e.preventDefault();
    document.querySelector("#heading-top").scrollIntoView();
  };

  const getShareUrl = () => {
    // eslint-disable-next-line no-restricted-globals
    return "https://www.principality.co.uk/mortgages/House-Price-Index";
  };

  const handleShareFacebook = () => {
    window.parent.postMessage(
      `event~HousePriceIndex~${location}~Facebook~0`,
      "*"
    );

    window.FB.ui(
      {
        method: "share",
        href: getShareUrl(),
        quote: t("location.facebookShareText"),
      },
      function (response) {}
    );
  };

  const handleShareTwitter = () => {
    window.parent.postMessage(
      `event~HousePriceIndex~${location}~Twitter~0`,
      "*"
    );
    const text = encodeURIComponent(t("location.twitterShareText"));
    window.open(
      "https://twitter.com/intent/tweet?text=" +
        text +
        "&url=" +
        getShareUrl() +
        "&hashtags=principality"
    );
  };

  const housePrices = detail.house_prices;

  // Let's add the images onto the house types.
  const houseTypes = housePrices.by_type.map((item, key) => ({
    ...item,
    image: houseTypeImages[key],
  }));

  // data-id is added so this can be queried via the DOM to scrollIntoView.
  return (
    <StyledLocationDetail
      data-id={id}
      increase={parseFloat(housePrices.annual_change) > 0 ? true : false}
      data-testid="location-detail"
      active={active}
      {...rest}
    >
      <Header data-testid="location-detail_header" onClick={handleToggle}>
        <ColumnHeader>
          <Number>{id + 1}</Number>
          <TitleWrapper>
            <Title>{t(`locations.${location}`, location)}</Title>
            <AveragePrice>
              {t("location.avgLabel")}{" "}
              {parseInt(housePrices.average).toLocaleString("en-GB", {
                style: "currency",
                currency: "GBP",
                minimumFractionDigits: 0,
              })}{" "}
            </AveragePrice>
          </TitleWrapper>
        </ColumnHeader>

        <PercentageChangeWrapper>
          <PercentageChange
            value={parseFloat(housePrices.annual_change)}
            color="#630E34"
          />
          <PercentageChange
            value={parseFloat(housePrices.quarter_change)}
            color="#B61356"
          />
        </PercentageChangeWrapper>

        <ToggleButton onClick={handleToggle}>{active ? "-" : "+"}</ToggleButton>
      </Header>

      {active && (
        <Content data-testid="location-detail_detail">
          <Prices>
            <PricesHeading>
              {t("location.avgHousePriceByTypeHeading")}
            </PricesHeading>
            {!isLargerDevice ? (
              <Carousel types={houseTypes} />
            ) : (
              <HouseTypeList types={houseTypes} />
            )}
          </Prices>
          <LivingCosts>
            <LivingCostsHeadlines>
              <LivingCostColumn>
                <Circle>£</Circle>
                <LivingCostHeading>
                  {t("location.councilTaxHeading")}
                </LivingCostHeading>
                <LivingCostValue>
                  {detail.council_tax_average.toLocaleString("en-GB", {
                    style: "currency",
                    currency: "GBP",
                    minimumFractionDigits: 0,
                  })}
                </LivingCostValue>
              </LivingCostColumn>
              <LivingCostColumn>
                <Circle>
                  <Image src={tickImage} />
                </Circle>
                <LivingCostHeading>
                  {t("location.lifeSatisfactionHeading")}
                </LivingCostHeading>
                <LivingCostValue>
                  {parseFloat(detail.life_satisfaction)}/10 (
                  {t(
                    lifeSatisifactionFriendly(
                      parseFloat(detail.life_satisfaction)
                    )
                  )}
                  )
                </LivingCostValue>
              </LivingCostColumn>
              <LivingCostColumn>
                <Circle>&pound;</Circle>
                <LivingCostHeading>
                  {t("location.avgSalaryHeading")}
                </LivingCostHeading>
                <LivingCostValue>
                  {isNaN(parseInt(detail.average_salary))
                    ? "N/A"
                    : parseInt(detail.average_salary).toLocaleString("en-GB", {
                        style: "currency",
                        currency: "GBP",
                        minimumFractionDigits: 0,
                      })}{" "}
                </LivingCostValue>
              </LivingCostColumn>
            </LivingCostsHeadlines>
            <ActionsWrapper>
              <ButtonWrapper>
                <Button onClick={handleBackToTop}>
                  {t("location.backToTopButtonText")}
                </Button>
              </ButtonWrapper>
              <SocialIcons>
                <ShareText>{t("location.shareLabelText")}</ShareText>
                <SocialButton onClick={handleShareFacebook}>
                  <SocialImage
                    src={FacebookIcon}
                    alt={t("location.buttonShareTextFacebook")}
                  />
                </SocialButton>
                <SocialButton onClick={handleShareTwitter}>
                  <SocialImage
                    src={TwitterIcon}
                    alt={t("location.buttonShareTextTwitter")}
                  />
                </SocialButton>
              </SocialIcons>
            </ActionsWrapper>
          </LivingCosts>
        </Content>
      )}
    </StyledLocationDetail>
  );
};

LocationDetail.propTypes = {
  active: PropTypes.bool,
  id: PropTypes.number.isRequired,
  location: PropTypes.string.isRequired,
  detail: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
};

LocationDetail.defaultProps = {
  active: false,
};

export default LocationDetail;
